import {
    apiHost,
    customerApiHost,
    rbacApiHost,
    Country,
    isGlobalEnv,
} from './config';
import {
    RequestConfig,
    AxiosRequestConfig,
    AxiosResponse,
    RequestError,
    AxiosError,
} from '@umijs/max';
import { message as Message } from 'antd';
// import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import { redirectToLogin } from './utils/redirectToLogin';
import formatParams from './utils/formatParams';
import Cookies from 'js-cookie';
import loadingDom from './components/loading';
import { getUuid } from '@/utils/common';

const request_source = isGlobalEnv ? 4 : 2;

Cookies.set('request_source', request_source, {
    domain: '.popmart.com',
    Secure: true,
    SameSite: 'None',
    Partitioned: true,
});

interface Config extends AxiosRequestConfig {
    noCertificate: boolean; // 不需要验签信息的接口，一般用于外部接口
    interceptor: boolean; // 自主处理接口数据 不走统一处理
}
let uniqueRequestId = ''; //创建售后单唯一值，用于优化请求，防止重复建单
// 当前正在请求的数量
let requestCount = 0;
// 延迟一段时间显示，避免页面闪烁
let delay = 200;
let showLoadingTimer: any = null;
function showLoading() {
    if (requestCount === 0 && !showLoadingTimer) {
        showLoadingTimer = setTimeout(() => {
            if (requestCount > 0) {
                let dom = document.createElement('div');
                dom.setAttribute('id', 'loading');
                document.body.appendChild(dom);
                const ReactDOM = createRoot(dom);
                ReactDOM.render(loadingDom);
            }
        }, delay);
    }
    requestCount++;
}

function hideLoading() {
    requestCount--;
    if (requestCount <= 0) {
        requestCount = 0;
        setTimeout(() => {
            if (document.getElementById('loading')) {
                document.body.removeChild(document.getElementById('loading'));
                showLoadingTimer = null;
            }
        }, delay);
    }
}

window.addEventListener('beforeunload', () => {
    sessionStorage.removeItem('uniqueRequestId');
});

interface Response extends Omit<AxiosResponse, 'config'> {
    config: Config;
}

type ApiRequestOptions = {
    /**
     * 显示错误[默认开启]
     */
    showApiError?: boolean;
    /**
     * 显示网络错误[默认开启]
     */
    showHttpError?: boolean;
    /**
     * 格式化结果[默认取result.data]
     */
    formatResult?: (result: any) => any;
};

export const httpStatusMessage: { [key: string]: string } = {
    200: '服务器成功返回请求的数据。',
    201: '新建或修改数据成功。',
    202: '一个请求已经进入后台排队（异步任务）。',
    204: '删除数据成功。',
    400: '发出的请求有错误，服务器没有进行新建或修改数据的操作。',
    401: '用户没有权限（令牌、用户名、密码错误）。',
    403: '用户得到授权，但是访问是被禁止的。',
    404: '发出的请求针对的是不存在的记录，服务器没有进行操作。',
    405: '请求方法不被允许。',
    406: '请求的格式不可得。',
    410: '请求的资源被永久删除，且不会再得到的。',
    422: '当创建一个对象时，发生一个验证错误。',
    500: '服务器发生错误，请检查服务器。',
    502: '网关错误。',
    503: '服务不可用，服务器暂时过载或维护。',
    504: '无网络，请检查网络后重试！',
    0: '无网络，请检查网络后重试！', // 无网络有时候是504，有时候状态是0直接发送失败
};

// 运行时配置
export const request: RequestConfig = {
    // 统一的请求设定
    timeout: isGlobalEnv ? 30000 : 10000,
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    withCredentials: true,
    // baseURL: process.env.NODE_ENV === 'development' ? '/api' : apiHost,
    // 请求拦截器
    requestInterceptors: [
        (config: Config) => {
            const { url, noCertificate = false, headers, params } = config;
            // console.log('requestInterceptors', config);

            showLoading();
            const first: string | undefined = url?.split('/')[1];
            const service_center_user_rbac_token = Cookies.get(
                'service_center_user_rbac_token',
            );

            // 是否不需要验签
            if (!noCertificate) {
                config.params = {
                    ...formatParams(params),
                    ...params,
                };
            }
            if (url?.indexOf('work-order/workOrder/work/create') > 0) {
                uniqueRequestId =
                    sessionStorage.getItem('uniqueRequestId') || getUuid();
                sessionStorage.setItem('uniqueRequestId', uniqueRequestId);
                config.data = {
                    uniqueRequestId,
                    ...config.data,
                };
            }
            if (process.env.NODE_ENV === 'development') {
                if (first && ['rbac', 'work-order'].includes(first)) {
                    config.baseURL = '/api';
                } else {
                    config.baseURL = '/customerApi';
                    if (!noCertificate) {
                        config.headers = {
                            service_center_user_rbac_token,
                            request_source,
                            ...(headers || {}),
                        };
                    }
                }

                if (isGlobalEnv) {
                    // 国际化站点
                    config.headers = {
                        request_source,
                        'X-Client-Country': Country.toUpperCase(),
                        Language: 'zh-hans',
                        ...config.headers,
                    };
                }

                return config;
            }

            if (first && ['work-order'].includes(first)) {
                config.baseURL = apiHost;
            } else if (first && ['rbac'].includes(first)) {
                config.baseURL = rbacApiHost;
            } else {
                config.baseURL = customerApiHost;
                // patch: 主域名不一致，拿不到cookie 目前兼容方式写入 header
                if (!noCertificate) {
                    config.headers = {
                        service_center_user_rbac_token,
                        request_source,
                        ...(headers || {}),
                    };
                }
            }

            if (isGlobalEnv) {
                // 国际化站点
                config.headers = {
                    ...config.headers,
                    request_source,
                    'X-Client-Country': Country.toUpperCase(),
                    Language: 'zh-hans',
                };
            }

            return config;
        },
    ],

    // 响应拦截器
    responseInterceptors: [
        (response: Response) => {
            // 拦截响应数据，进行个性化处理
            // console.log('response', response);
            const { config } = response;
            hideLoading();

            if (config.interceptor) {
                return response;
            }

            const res = <ApiDataType<any>>response.data;

            // api错误
            // if (res.code !== 0 || (res.return_code && res.return_code !== 1)) {
            //     res.success = false;
            // }
            if (res.code === 0 || (res.return_code && res.return_code === 1)) {
                res.code = 0;
                if (
                    config?.url?.indexOf('work-order/workOrder/work/create') > 0
                ) {
                    sessionStorage.removeItem('uniqueRequestId');
                }
            } else {
                res.success = false;
            }

            return response;
        },
    ],

    // 错误处理
    errorConfig: {
        // 错误抛出
        errorThrower: (res: ApiDataType<any>) => {
            if (res.success === false && res.error !== 'success') {
                const error = new Error(res.message || res.error);
                error.name = 'ApiError';
                error.cause = { code: res.code || res.return_code };
                throw error;
            }
        },
        // 错误接收及处理
        errorHandler: (
            error: RequestError,
            opts: AxiosRequestConfig & ApiRequestOptions,
        ) => {
            const { showApiError = true, showHttpError = true } = opts;
            hideLoading();
            let message;
            if (error.name === 'ApiError') {
                // api 错误
                message = error.message;
                if (showApiError) Message.error(message);
                // token 失效跳转到登录页
                if (error.cause!.code === 40004) {
                    sessionStorage.removeItem('uniqueRequestId');
                    setTimeout(() => {
                        if (window.innerEvent) {
                            Cookies.remove('service_center_user_rbac_token');
                            Cookies.remove('service_center_user_rbac_token', {
                                domain: '.popmart.com',
                                Secure: true,
                                SameSite: 'None',
                                Partitioned: true,
                            });
                            window.innerEvent.logout();
                            return;
                        }
                        redirectToLogin();
                    }, 1500);
                }
            } else {
                // http 错误
                const axiosError = <AxiosError>error;
                if (axiosError.response) {
                    message =
                        httpStatusMessage[axiosError.response.status] ||
                        axiosError.response.statusText;
                }
                if (showHttpError) Message.error(message || '未知错误');
            }
        },
    },
};
